import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { Globe, ChevronDown, Menu, X, User } from "lucide-react";
import {
  getAuthChangeEventName,
  getAuthToken,
  isAuthenticated,
} from "../utils/authSession";
import { getUserDashboard } from "../services/userService";
import { hasActiveSubscription as hasActiveSubscriptionStatus } from "../utils/subscriptionStatus";

const languages = [
  { code: "EN", name: "English", gtCode: "en" },
  { code: "SV", name: "Svenska", gtCode: "sv" },
  { code: "ES", name: "Español", gtCode: "es" },
  { code: "DE", name: "Deutsch", gtCode: "de" },
];

const LANGUAGE_STORAGE_KEY = "learnweb_selected_language";

const getStoredLanguage = () => {
  const storedLanguage = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
  return languages.find((language) => language.code === storedLanguage) || languages[0];
};

const getTranslatedLanguage = () => {
  const googTransCookie = document.cookie
    .split("; ")
    .find((cookie) => cookie.startsWith("googtrans="));
  const translatedCode = googTransCookie?.split("/").pop();

  return languages.find((language) => language.gtCode === translatedCode) || getStoredLanguage();
};

const getCurrentGoogleTranslateCode = () => {
  return document.cookie
    .split("; ")
    .find((cookie) => cookie.startsWith("googtrans="))
    ?.split("/")
    .pop();
};

const ensureGoogleTranslate = () => {
  if (!document.getElementById("google_translate_element")) {
    const container = document.createElement("div");
    container.id = "google_translate_element";
    container.style.position = "absolute";
    container.style.left = "-9999px";
    container.style.top = "0";
    container.style.width = "1px";
    container.style.height = "1px";
    container.style.overflow = "hidden";
    document.body.appendChild(container);
  }

  window.googleTranslateElementInit = () => {
    const googleApi = (window as Window & { google?: typeof google }).google;

    if (!googleApi?.translate?.TranslateElement) {
      return;
    }

    new googleApi.translate.TranslateElement(
      { pageLanguage: "en", autoDisplay: false },
      "google_translate_element"
    );
  };

  const googleTranslate = (window as Window & { google?: typeof google }).google?.translate;

  if (
    document.querySelector('script[src*="translate_a/element.js"]') &&
    googleTranslate?.TranslateElement
  ) {
    window.googleTranslateElementInit();
    return;
  }

  if (document.querySelector('script[src*="translate_a/element.js"]')) {
    return;
  }

  const script = document.createElement("script");
  script.src =
    "https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
  script.async = true;
  document.body.appendChild(script);
};

const Header = () => {
  const [isLangOpen, setIsLangOpen] = useState(false);
  const [selectedLang, setSelectedLang] = useState(() => getTranslatedLanguage().code);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [loggedIn, setLoggedIn] = useState(isAuthenticated());
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const [isCheckingSubscription, setIsCheckingSubscription] = useState(false);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const pendingGoogleTranslateCodeRef = useRef<string | null>(null);

  const changeLanguage = (gtCode: string, attempt = 0) => {
    const select = document.querySelector<HTMLSelectElement>(".goog-te-combo");
    if (!select) {
      if (attempt < 20) {
        window.setTimeout(() => changeLanguage(gtCode, attempt + 1), 250);
      }
      return;
    }

    select.value = gtCode;
    select.dispatchEvent(new Event("change", { bubbles: true }));

    if (getCurrentGoogleTranslateCode() === gtCode) {
      pendingGoogleTranslateCodeRef.current = null;
      return;
    }

    if (attempt < 20) {
      window.setTimeout(() => changeLanguage(gtCode, attempt + 1), 250);
    } else {
      pendingGoogleTranslateCodeRef.current = null;
    }
  };

  const handleLangChange = (lang: (typeof languages)[0]) => {
    setSelectedLang(lang.code);
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, lang.code);
    pendingGoogleTranslateCodeRef.current = lang.gtCode;
    changeLanguage(lang.gtCode);
    setIsLangOpen(false);
  };

  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  /* Load Google Translate widget */
  useEffect(() => {
    ensureGoogleTranslate();
  }, []);

  /* Close dropdown on outside click */
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsLangOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  /* Keep header label synced with Google Translate changes */
  useEffect(() => {
    const syncSelectedLanguage = () => {
      const currentGoogleTranslateCode = getCurrentGoogleTranslateCode();

      if (
        pendingGoogleTranslateCodeRef.current &&
        currentGoogleTranslateCode !== pendingGoogleTranslateCodeRef.current
      ) {
        return;
      }

      pendingGoogleTranslateCodeRef.current = null;
      const translatedLanguage = getTranslatedLanguage();
      setSelectedLang(translatedLanguage.code);
      window.localStorage.setItem(LANGUAGE_STORAGE_KEY, translatedLanguage.code);
    };

    syncSelectedLanguage();
    const intervalId = window.setInterval(syncSelectedLanguage, 1000);

    return () => window.clearInterval(intervalId);
  }, []);

  /* Lock body scroll on mobile menu */
  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? "hidden" : "unset";
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isMobileMenuOpen]);

  /* Listen auth changes */
  useEffect(() => {
    const refreshAuth = () => setLoggedIn(isAuthenticated());
    const authEventName = getAuthChangeEventName();

    window.addEventListener("storage", refreshAuth);
    window.addEventListener(authEventName, refreshAuth);

    return () => {
      window.removeEventListener("storage", refreshAuth);
      window.removeEventListener(authEventName, refreshAuth);
    };
  }, []);

  /* Check subscription */
  useEffect(() => {
    const checkSubscription = async () => {
      if (!loggedIn) {
        setHasActiveSubscription(false);
        setIsCheckingSubscription(false);
        return;
      }

      try {
        setIsCheckingSubscription(true);
        const token = getAuthToken();
        if (!token) {
          setHasActiveSubscription(false);
          return;
        }

        const { data } = await getUserDashboard(token);
        setHasActiveSubscription(hasActiveSubscriptionStatus(data?.subscriptions || []));
      } catch {
        setHasActiveSubscription(false);
      } finally {
        setIsCheckingSubscription(false);
      }
    };

    checkSubscription();
  }, [loggedIn]);

  return (
    <header className="bg-[#113A28] text-white py-4 px-6 md:px-12 relative z-50">
      <div className="flex items-center justify-between mx-auto max-w-7xl">

        {/* Logo */}
        <Link to="/" className="relative z-[60]">
          <img
            src="/LOGO-web-academy.svg"
            alt="Learnwebacademy Logo"
            width="234"
            height="45"
            className="w-auto h-8"
          />
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-gray-200">
          <Link to="/" className="hover:text-white">Home</Link>
          <Link to={loggedIn ? "/library" : "/pricing"} className="hover:text-white">
            Library
          </Link>

          {(!loggedIn || (!isCheckingSubscription && !hasActiveSubscription)) && (
            <Link to="/pricing" className="hover:text-white">Pricing</Link>
          )}

          <Link to="/unsubscribe" className="hover:text-white">
            Unsubscribe
          </Link>
        </nav>

        {/* Desktop Right */}
        <div className="hidden md:flex items-center gap-6">

          {/* Language */}
          <div ref={dropdownRef} className="relative notranslate" translate="no">
            <button
              type="button"
              onClick={() => setIsLangOpen((prev) => !prev)}
              className="flex items-center gap-1.5 text-sm font-bold text-gray-200 hover:text-white"
              translate="no"
              aria-expanded={isLangOpen}
              aria-haspopup="listbox"
            >
              <Globe size={16} className="text-white" />
              {selectedLang}
              <ChevronDown
                size={14}
                className={`transition ${isLangOpen ? "rotate-180" : ""}`}
              />
            </button>

            {isLangOpen && (
              <div
                className="absolute right-0 mt-5 w-36 bg-white rounded-2xl shadow-lg border py-2"
                role="listbox"
                aria-label="Select language"
              >
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    type="button"
                    onClick={() => handleLangChange(lang)}
                    translate="no"
                    role="option"
                    aria-selected={selectedLang === lang.code}
                    className={`w-full text-left px-5 py-2.5 text-sm font-bold hover:bg-[#4CE470]/10 ${
                      selectedLang === lang.code
                        ? "text-[#113A28] bg-gray-50"
                        : "text-gray-500"
                    }`}
                  >
                    {lang.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Profile */}
          {loggedIn && (
            <Link
              to="/my-account"
              className="flex items-center gap-1.5 text-sm font-bold text-gray-200 hover:text-[#4CE470]"
            >
              <User size={18} />
            </Link>
          )}

          {/* Login */}
          {!loggedIn && (
            <Link
              to="/login?mode=login"
              className="bg-[#4CE470] text-[#113A28] px-5 py-2.5 rounded-full font-bold text-sm hover:bg-[#3bc45d]"
            >
              LOGIN / SIGN UP
            </Link>
          )}
        </div>

        {/* Mobile Toggle */}
        <button
          className="md:hidden z-[60]"
          onClick={() => setIsMobileMenuOpen((p) => !p)}
        >
          {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
        </button>
      </div>

      {/* Mobile Menu */}
      <div
        className={`fixed inset-0 bg-[#0A1F15] z-50 flex flex-col pt-24 px-6 pb-8 md:hidden transition-transform duration-300 ${
          isMobileMenuOpen ? "translate-y-0" : "-translate-y-full"
        }`}
      >
        <nav className="flex flex-col gap-6 mb-auto text-xl font-bold">

          <Link to="/" onClick={closeMobileMenu}>Home</Link>

          <Link
            to={loggedIn ? "/library" : "/pricing"}
            onClick={closeMobileMenu}
          >
            Library
          </Link>

          {(!loggedIn || (!isCheckingSubscription && !hasActiveSubscription)) && (
            <Link to="/pricing" onClick={closeMobileMenu}>
              Pricing
            </Link>
          )}

          <Link to="/unsubscribe" onClick={closeMobileMenu}>
            Unsubscribe
          </Link>
        </nav>

        <div className="flex flex-col gap-6 mt-8">

          {loggedIn && (
            <Link
              to="/my-account"
              onClick={closeMobileMenu}
              className="flex justify-center items-center gap-2 py-4 bg-white/10 rounded-full"
            >
              <User size={24} />
              PROFILE
            </Link>
          )}

          {!loggedIn && (
            <Link
              to="/login?mode=signup"
              onClick={closeMobileMenu}
              className="flex justify-center py-4 bg-[#4CE470] text-[#113A28] rounded-full font-bold"
            >
              LOGIN / SIGN UP
            </Link>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
